import type { SpecMetafieldRow } from '../ShopifySpecMetafieldsSection';

/** What the store may keep; mirrors `SPEC_METAFIELD_MAPPING_MAX_ROWS` on the server, which enforces it. */
export const SPEC_MAPPING_FALLBACK_MAX = 500;

export type SpecCrosswalkMode = 'merge' | 'replace';

const labelKey = (row: SpecMetafieldRow) => row.specLabel.trim().toLowerCase();
const targetKey = (row: SpecMetafieldRow) => `${row.namespace}.${row.key}`.toLowerCase();

export interface SpecCrosswalkMergeResult {
  rows: SpecMetafieldRow[];
  /** Imported rows that were not in the table, or mapped to a different metafield. */
  added: number;
  changed: number;
  /** Table rows removed: replaced in `replace` mode, or sharing a spec or metafield with an imported row. */
  removed: number;
  /** Table rows that already matched an imported row. */
  unchanged: number;
  /** The result is longer than the store may keep; the server drops the surplus on Save. */
  overLimit: boolean;
}

/**
 * Loads the validated rows of an import into the table. `merge` keeps every table row the file does not
 * touch; an imported row wins over a table row for the same spec label, and over one using the same
 * metafield (a metafield takes one spec). `replace` makes the file the whole table. Blank draft rows
 * (a row added in the table and not filled in yet) are kept in `merge`.
 */
export function applySpecCrosswalk(
  current: SpecMetafieldRow[],
  imported: SpecMetafieldRow[],
  mode: SpecCrosswalkMode,
  max: number = SPEC_MAPPING_FALLBACK_MAX,
): SpecCrosswalkMergeResult {
  const importedByLabel = new Map(imported.map((row) => [labelKey(row), row]));
  const importedTargets = new Set(imported.map(targetKey));
  const filled = current.filter((row) => row.specLabel.trim());
  let added = 0;
  let changed = 0;
  let unchanged = 0;
  for (const row of imported) {
    const existing = filled.find((candidate) => labelKey(candidate) === labelKey(row));
    if (!existing) added += 1;
    else if (targetKey(existing) === targetKey(row)) unchanged += 1;
    else changed += 1;
  }
  const kept = mode === 'replace'
    ? []
    : current.filter((row) => {
        if (!row.specLabel.trim()) return true;
        return !importedByLabel.has(labelKey(row)) && !importedTargets.has(targetKey(row));
      });
  const removed = filled.filter((row) => !kept.includes(row)).length - changed - unchanged;
  const rows = [...kept, ...imported];
  return { rows, added, changed, removed: Math.max(removed, 0), unchanged, overLimit: rows.length > max };
}
